import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import useMediaQuery from '@mui/material/useMediaQuery';
import PageTitle from '@features/PageTitle';
import PageHeader from '@features/PageHeader';
import SubscribeToMessagesSnackbar from '@features/SubscribeToMessagesSnackbar';
import Button from '@components/Button';
import {
    channelsActions,
    conversationsActions,
    conversationTagsActions,
    inboxActions,
    labelsActions,
    accountActions,
    agentsActions,
} from '@actions';
import { hubService } from '@services';
import { ConversationStatus, AssignedStatus, ConversationSorting } from '@constants/conversations';
import { AccIntegrationName } from '@constants/integrations';
import { isIntegrationConnected } from '@helpers/integrations';
import { buildAssigneesForView, clearedInboxFilters } from '@helpers/inbox';
import { WsHubMessage } from '@constants/ws-hub';
import { CHANNEL } from '@constants/channels';
import { MessageStatus, AssignmentType } from '@constants/inbox';
import usePermissions from '@hooks/usePermissions';
import {
    useTranslation,
    useQueryParams,
    useChannels,
    useConversations,
    deriveConversationRead,
    useInboxTeams,
    useTeamMove,
    userCoversTeam,
} from '@hooks';
import { InboxEmptyIcon } from 'assets/images/icons';
import { RoutePath } from '@constants/routes';
import ContactInfo from './ContactInfo';
import Dialogs from './Dialogs';
import Inbox from './Inbox';
import NavigationRail from './NavigationRail';
import { Container, EmptyLayout, PageLayout, PageGrid } from './styles';

const ViewQueryNames = {
    [AssignedStatus.Mine]: 'mine',
    [AssignedStatus.Unassigned]: 'unassigned',
    [AssignedStatus.All]: 'all',
};

// Not an assignment value — the Collaborating view scopes by per-conversation collaborator grants (filter.collaboratingOnly).
const COLLABORATING_VIEW = 'collaborating';

const parseAssignmentParam = value => {
    const assignment = value ? +value : NaN;
    return Object.values(AssignedStatus).includes(assignment) ? assignment : null;
};

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const InboxPage = () => {
    const params = useQueryParams();
    const queryConversationId = params.get('conversationId');
    const queryConversationAssignment = parseAssignmentParam(params.get('assignment'));

    const [filter, setFilter] = useState({
        channels: [],
        teams: [], // INFO: empty = no team filter
        statuses: [ConversationStatus.Open],
        conversationId: queryConversationId || null,
        assignment: queryConversationAssignment ?? AssignedStatus.Mine,
        assignees: [],
        showUnreadOnly: false,
        collaboratingOnly: false,
        tags: [],
        timeframe: { start: null, end: null }, // INFO: ui state
        from: null, // INFO: for API
        to: null, // INFO: for API
        sorting: ConversationSorting.ActivityNewestFirst,
        assignedToPulse: false,
    });
    const [activeConversation, setActiveConversation] = useState(null);
    const [contactOpened, setContactOpened] = useState(false);

    const { data: messages } = useSelector(state => state.inbox);
    const {
        data: conversations,
        counters,
        loading: conversationsLoading,
        archived,
        archivedLoading,
    } = useSelector(state => state.conversations);
    const { data: channels, loading: channelsLoading } = useSelector(state => state.channels);
    const currentUser = useSelector(state => state.auth.user);
    const users = useSelector(state => state.account?.account?.users || []);
    const account = useSelector(state => state.account?.account);
    const { data: agents, loading: agentsLoading } = useSelector(state => state.agentsLookup);
    const { data: conversationTags, loaded: tagsLoaded } = useSelector(state => state.conversationTags);

    const isMobile = useMediaQuery('@media (max-width: 768px)');
    const isMobileAndContactOpened = useMediaQuery('@media (max-width: 1024px)');
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { getAccessibleChannelIds } = useChannels();
    const { teams: accessibleTeams, teamsLoaded, isInboxAdmin } = useInboxTeams();
    const {
        conversationsRef,
        isConversationMatchesFilter,
        updateConversationInFeedAndReturn,
        upsertConversationInFeed,
        updateConversationsChannelData,
        removeConversationById,
    } = useConversations();

    const firstRenderRef = useRef(true);
    const activeConversationRef = useRef(null);
    const filterRef = useRef(null);
    const channelsRef = useRef([]);
    const delayedMessagesRef = useRef([]);
    const onTeamMovedRef = useRef(null);
    const currentUserRef = useRef(null);
    const usersRef = useRef([]);

    const { hasFullAccess } = usePermissions();
    const isUser = !hasFullAccess('settings');
    const isWhatsAppConnected = isIntegrationConnected(account, AccIntegrationName.WhatsApp);

    useEffect(() => {
        /*
            INFO:It's necessary to prevent calling hubService.subscribeToEvents callbacks
            multiple times. Each time useEffect() is triggered, it will call hubService.subscribeToEvents,
            and all callbacks will be called the same number times. hub.off(event) method 
            removed each callback for event, so other subscribed components will not receive the event.
        */
        // INFO: when messages change, update messages with delayed statuses
        if (delayedMessagesRef.current.length) {
            const delayed = delayedMessagesRef.current;
            delayedMessagesRef.current = [];
            delayed.forEach(payload => dispatch(inboxActions.updateMessageStatus(payload)));
        }
        activeConversationRef.current = activeConversation;
        filterRef.current = filter;
        channelsRef.current = channels;
    }, [messages, activeConversation, filter, channels]);

    const onTeamMoved = useTeamMove({ setActiveConversation, filterRef });
    useEffect(() => {
        // The SignalR subscription is registered once; the refs keep the handlers'
        // Redux snapshot (current user, conversations) fresh across renders.
        onTeamMovedRef.current = onTeamMoved;
        currentUserRef.current = currentUser;
        usersRef.current = users;
    });

    const validateTeamParam = value => {
        if (!value || !GUID_PATTERN.test(value)) {
            return null;
        }
        // An admin's full team list loads async; until then trust the well-formed id —
        if (isInboxAdmin && !teamsLoaded) {
            return value;
        }
        return accessibleTeams.some(team => team.id === value) ? value : null;
    };

    useEffect(() => {
        const { assignment, conversationId } = filterRef.current;
        // A team can receive moved conversations without owning any channel, so the
        // initial load waits only for the lookups to finish, not for channels to exist.
        if (firstRenderRef.current && !channelsLoading && !archivedLoading && !agentsLoading) {
            // Channels from the API are already scoped to the user's teams; the list
            // query itself is only team-scoped, so an empty channel selection keeps
            // conversations reachable after a handover to a team without the channel.
            const channelsIds = [
                ...getAccessibleChannelIds(c => c?.incoming),
                ...(archived > 0 ? [CHANNEL.ARCHIVED] : []),
            ];
            // INFO: rail scope is shareable via ?view= / ?team= / ?channel=
            const queryCollaborating = params.get('view') === COLLABORATING_VIEW;
            const queryView = Object.keys(ViewQueryNames).find(key => ViewQueryNames[key] === params.get('view'));
            const queryTeam = validateTeamParam(params.get('team'));
            const queryChannel = queryTeam ? null : params.get('channel');
            const scopedChannels =
                queryChannel === 'archived' && archived > 0
                    ? [CHANNEL.ARCHIVED]
                    : queryChannel && channelsIds.includes(queryChannel)
                      ? [queryChannel]
                      : [];
            const tagParam = params.get('tag');
            const queryTag = !queryTeam && !queryChannel && tagParam && GUID_PATTERN.test(tagParam) ? tagParam : null;
            const effectiveAssignment =
                queryView !== undefined
                    ? +queryView
                    : queryTeam || queryChannel || queryTag || queryCollaborating
                      ? AssignedStatus.All
                      : assignment;
            // INFO: set to ALL if there is conversationId
            const updatedAssignment = conversationId ? AssignedStatus.All : effectiveAssignment;
            const assignees = assigneesForView(updatedAssignment);
            const filterValues = {
                ...filterRef.current,
                channels: scopedChannels,
                teams: queryTeam ? [queryTeam] : filterRef.current.teams,
                tags: queryTag ? [queryTag] : filterRef.current.tags,
                assignment: updatedAssignment,
                assignees,
                collaboratingOnly: queryCollaborating,
            };
            setFilter(filterValues);
            dispatch(conversationsActions.getCounters(filterValues));
            dispatch(conversationsActions.get(filterValues));
            firstRenderRef.current = false;
        }
    }, [channels, archived, channelsLoading, archivedLoading, agentsLoading]);

    useEffect(() => {
        // INFO: need to update filter after redirect from activity/notifications if inbox is opened
        const { conversationId } = filterRef.current;
        if (!firstRenderRef.current && queryConversationId && queryConversationId !== conversationId) {
            const filterValues = {
                ...filterRef.current,
                conversationId: queryConversationId,
                assignment: AssignedStatus.All,
                assignees: [
                    ...users.map(u => ({ value: u.id, type: AssignmentType.User })),
                    { value: null, type: AssignmentType.Unassigned },
                    ...agents.map(a => ({ value: a.id, type: AssignmentType.Agent })),
                ],
            };
            setFilter(filterValues);
            dispatch(conversationsActions.get(filterValues));
        }
    }, [queryConversationId]);

    useEffect(() => {
        hubService.subscribeToEvents({
            [WsHubMessage.InboxMessageCreated]: payload => {
                if (!firstRenderRef.current) {
                    const { conversation, message } = payload;
                    const isActiveConversation = activeConversationRef.current?.id === conversation?.id;
                    // Shared broadcast can't express per-recipient read — derive it from views, or a phantom unread shows until the next refetch.
                    const read =
                        isActiveConversation || deriveConversationRead(conversation, currentUserRef.current?.id);
                    const readConversation = { ...conversation, read };
                    // INFO: if selected conversation -> update inbox
                    if (isActiveConversation) {
                        setActiveConversation(prev => ({ ...prev, ...readConversation }));
                        const updatedMessage = {
                            ...message,
                            createdAt: message.createdAt.substring(0, message.createdAt.length - 2) + 'Z',
                        };
                        dispatch(inboxActions.addMessage(conversation.id, updatedMessage));
                    }

                    const isConversationOld = updateConversationInFeedAndReturn(readConversation, filterRef.current);
                    if (isConversationOld) {
                        return;
                    }

                    // INFO: if conversation is new, add it to the list
                    dispatch(conversationsActions.update([...conversationsRef.current, readConversation]));
                    dispatch(conversationsActions.getCounters(filterRef.current));
                }
            },
            [WsHubMessage.InboxConversationUpdated]: payload => {
                const { conversation, changeKind } = payload;
                if (!conversation) {
                    return;
                }
                const isActiveConversation = activeConversationRef.current?.id === conversation.id;
                if (changeKind === 'team') {
                    onTeamMovedRef.current?.(conversation, { isActive: isActiveConversation });
                    return;
                }
                // Removed collaborator grant that was this client's only access — drop it, like losing team access on a move.
                if (
                    changeKind === 'collaborators' &&
                    !(conversation.collaborators || []).includes(currentUserRef.current?.id) &&
                    !userCoversTeam(currentUserRef.current, conversation.teamId)
                ) {
                    if (isActiveConversation) {
                        setActiveConversation(null);
                    }
                    removeConversationById(conversation.id);
                    dispatch(conversationsActions.getCounters(filterRef.current));
                    return;
                }
                const read = isActiveConversation || deriveConversationRead(conversation, currentUserRef.current?.id);
                const updatedConversation = { ...conversation, read };
                if (isActiveConversation) {
                    if (changeKind === 'tags' && !isConversationMatchesFilter(updatedConversation, filterRef.current)) {
                        setActiveConversation(null);
                    } else {
                        setActiveConversation(prev => ({ ...prev, ...updatedConversation }));
                    }
                }
                upsertConversationInFeed(updatedConversation, filterRef.current);
            },
            [WsHubMessage.InboxChannelTeamChanged]: payload => {
                // A channel-level team move can touch many conversations at once;
                // refetch the scope like the reconnect path instead of patching
                // item by item. Channel ownership is team-scoped, so the rail's
                // channel list changes too.
                dispatch(conversationsActions.get(filterRef.current));
                dispatch(conversationsActions.getCounters(filterRef.current));
                dispatch(channelsActions.get());

                // The open thread moved with its channel unless it was escalated
                // to another team; the shared aftermath keeps or drops it exactly
                // like a direct handover
                const active = activeConversationRef.current;
                const activeMoved =
                    active?.channelId === payload.channelId &&
                    (active.teamId === payload.previousTeamId || active.teamId == null);
                if (activeMoved) {
                    // Mirror the server: an open conversation loses an assignee who
                    // cannot access the new team, so the composer does not stay
                    // locked behind a stale assignment
                    const assigneeLosesAccess =
                        active.status === ConversationStatus.Open &&
                        active.assignedUserId != null &&
                        !userCoversTeam(
                            usersRef.current.find(u => u.id === active.assignedUserId),
                            payload.currentTeamId,
                        );
                    onTeamMovedRef.current?.(
                        {
                            ...active,
                            teamId: payload.currentTeamId,
                            teamName: payload.currentTeamName,
                            assignedUserId: assigneeLosesAccess ? null : active.assignedUserId,
                        },
                        { isActive: true },
                    );
                }
            },
            [WsHubMessage.InboxTagsUpdated]: () => {
                dispatch(conversationTagsActions.get());
                dispatch(conversationsActions.get(filterRef.current, null, conversationsRef.current.length));
                dispatch(conversationsActions.getCounters(filterRef.current));
            },
            [WsHubMessage.InboxConversationStatus]: payload => {
                const { conversationId, messagingWindowClosed } = payload;
                const isActiveConversation = activeConversationRef.current?.id === conversationId;
                // INFO: update active conversation if it matches
                if (isActiveConversation) {
                    setActiveConversation(prev => ({ ...prev, messagingWindowClosed }));
                }
                // INFO: update conversation in feed if it exists
                const conversation = conversationsRef.current.find(c => c.id === conversationId);
                if (conversation) {
                    updateConversationInFeedAndReturn({ ...conversation, messagingWindowClosed }, filterRef.current);
                }
            },
            [WsHubMessage.InboxMessageStatus]: payload => {
                const { conversation, status } = payload;
                // INFO: update message and selected conversation if conversation ids match
                const isActiveConversation = activeConversationRef.current?.id === conversation?.id;
                if (isActiveConversation) {
                    setActiveConversation(prev => ({ ...prev, ...conversation }));
                    if (!dispatch(inboxActions.updateMessageStatus(payload))) {
                        // INFO: put payload into queue to update later if message not found
                        delayedMessagesRef.current.push(payload);
                    }
                }
                // INFO: update conversation in feed
                if (status === MessageStatus.Delivered) {
                    updateConversationInFeedAndReturn(conversation, filterRef.current);
                }
            },
            [WsHubMessage.InboxChannelStatus]: async payload => {
                const { channelId, isDeleted, channelStatus } = payload;
                // INFO: update current conversation if channel matches
                if (activeConversationRef?.current?.channelId === channelId) {
                    setActiveConversation(prev => ({
                        ...prev,
                        channelStatus,
                        channelId: isDeleted ? null : channelId,
                    }));
                }
                // INFO: update locations with channels
                await dispatch(accountActions.get());
                // INFO: updated conversations feed if channel is deleted or updated
                updateConversationsChannelData(channelId, channelStatus, isDeleted);
                // INFO: updated channels
                const channelsCopy = channelsRef.current.slice();
                const channelIndex = channelsCopy?.findIndex(c => c.id === channelId);
                if (channelIndex !== -1) {
                    channelsCopy[channelIndex].status = channelStatus;
                    if (isDeleted) {
                        channelsCopy[channelIndex].id = null;
                        await dispatch(conversationsActions.getArchivedCounters());
                        // INFO: update channels in filter
                        setFilter(prev => ({ ...prev, channels: prev.channels.filter(id => id !== channelId) }));
                    }
                    dispatch(channelsActions.update(channelsCopy));
                }
            },
        });
        const onReconnected = () => {
            dispatch(conversationsActions.get(filterRef.current, null, conversationsRef.current.length));
            dispatch(conversationsActions.getCounters(filterRef.current));
            dispatch(inboxActions.getHistory(activeConversationRef.current?.id, false));
            dispatch(conversationTagsActions.get());
        };
        hubService.addReconnectedCallback(onReconnected);
        return () => {
            hubService.unsubscribeFromEvents([
                WsHubMessage.InboxMessageCreated,
                WsHubMessage.InboxMessageStatus,
                WsHubMessage.InboxConversationStatus,
                WsHubMessage.InboxConversationUpdated,
                WsHubMessage.InboxChannelTeamChanged,
                WsHubMessage.InboxChannelStatus,
                WsHubMessage.InboxTagsUpdated,
            ]);
            hubService.removeReconnectedCallback(onReconnected);
        };
    }, [dispatch]);

    useEffect(() => {
        dispatch(conversationsActions.getArchivedCounters());
        dispatch(channelsActions.get());
        dispatch(agentsActions.getLookup());
        dispatch(labelsActions.get());
        dispatch(conversationTagsActions.get());
        if (isWhatsAppConnected) {
            dispatch(channelsActions.getWhatsAppBusinessTemplates());
        }
        return () => {
            dispatch(conversationsActions.reset());
        };
    }, [dispatch, isWhatsAppConnected]);

    const syncScopeUrl = newFilter => {
        const searchParams = new URLSearchParams();
        if (newFilter.collaboratingOnly) {
            searchParams.set('view', COLLABORATING_VIEW);
        } else if (ViewQueryNames[newFilter.assignment]) {
            searchParams.set('view', ViewQueryNames[newFilter.assignment]);
        }
        if (newFilter.teams?.length === 1) {
            searchParams.set('team', newFilter.teams[0]);
        }
        if (newFilter.channels?.length === 1) {
            searchParams.set(
                'channel',
                newFilter.channels[0] === CHANNEL.ARCHIVED ? 'archived' : newFilter.channels[0],
            );
        }
        if (newFilter.tags?.length === 1) {
            searchParams.set('tag', newFilter.tags[0]);
        }
        const search = searchParams.toString();
        navigate({ search: search ? `?${search}` : '' }, { replace: true });
    };

    const assigneesForView = view => buildAssigneesForView(view, { users, agents, currentUserId: currentUser?.id });

    const applyScope = updates => {
        const newFilter = { ...filterRef.current, conversationId: null, ...updates };
        // An out-of-scope thread must not stay open with its actions available; the
        // list auto-selects the first conversation of the new scope once it loads
        if (activeConversationRef.current && !isConversationMatchesFilter(activeConversationRef.current, newFilter)) {
            setActiveConversation(null);
        }
        setFilter(newFilter);
        dispatch(conversationsActions.getCounters(newFilter));
        dispatch(conversationsActions.get(newFilter));
        syncScopeUrl(newFilter);
    };

    const buildScope = updates => ({
        teams: [],
        channels: [],
        tags: [],
        collaboratingOnly: false,
        assignment: AssignedStatus.All,
        assignees: assigneesForView(AssignedStatus.All),
        ...updates,
    });

    // Team, tag and Collaborating badges are open-only totals that ignore the unread
    // and timeframe filters, so their scope drops everything the badge did not count.
    // The Views badges do follow the active filter and Archived holds only resolved
    // conversations, so those two keep whatever the user has selected.
    const buildBadgeScope = updates =>
        buildScope({ statuses: [ConversationStatus.Open], ...clearedInboxFilters(), ...updates });

    const scopeHandlers = {
        onSelectView: view => applyScope(buildScope({ assignment: view, assignees: assigneesForView(view) })),
        onSelectTeam: teamId => applyScope(buildBadgeScope({ teams: teamId ? [teamId] : [] })),
        onSelectChannel: channelId => applyScope(buildScope({ channels: channelId === undefined ? [] : [channelId] })),
        onSelectTag: tagId => applyScope(buildBadgeScope({ tags: tagId ? [tagId] : [] })),
        onSelectCollaborating: () => applyScope(buildBadgeScope({ collaboratingOnly: true })),
    };

    useEffect(() => {
        // A shared ?team= link can point at a team this user cannot scope by — clear
        // it instead of silently pinning the inbox to an empty hidden scope.
        const scopedTeamId = filter.teams?.[0];
        if (firstRenderRef.current || !scopedTeamId || (isInboxAdmin && !teamsLoaded)) {
            return;
        }
        if (!accessibleTeams.some(team => team.id === scopedTeamId)) {
            applyScope({ teams: [] });
        }
    }, [accessibleTeams, teamsLoaded, filter.teams]);

    useEffect(() => {
        const scopedTagId = filter.tags?.[0];
        if (firstRenderRef.current || !scopedTagId || !tagsLoaded) {
            return;
        }
        if (!conversationTags.some(tag => tag.id === scopedTagId)) {
            applyScope({ tags: [] });
        }
    }, [conversationTags, tagsLoaded, filter.tags]);

    const onCloseContact = () => setContactOpened(false);

    const onDeleteContactCallback = async () => {
        setContactOpened(false);
        setActiveConversation(null);
        await dispatch(conversationsActions.get(filterRef.current, null, conversationsRef.current.length));
        await dispatch(conversationsActions.getCounters(filterRef.current));
    };

    const onMergeContactCallback = (data, contactId) => {
        const updatedContact = {
            contactId: data?.id,
            firstName: data?.firstName,
            lastName: data?.lastName,
            phoneNumber: data?.phoneNumber,
            email: data?.email,
        };
        const updatedConversations = conversationsRef.current.map(conversation => {
            return conversation.contactId === contactId || conversation.contactId === data?.id
                ? { ...conversation, ...updatedContact }
                : conversation;
        });
        dispatch(conversationsActions.update(updatedConversations));
        setActiveConversation(prev => ({ ...prev, ...updatedContact }));
    };

    // A channel-less team still gets moved conversations, so the inbox renders whenever
    // there is anything to show; the connect-channels page is only for truly empty
    // accounts. hasAnyConversations is status-independent — resolved-only history counts.
    const hasInboxContent =
        channels.length > 0 || archived > 0 || conversations.length > 0 || !!counters?.hasAnyConversations;

    return (
        <Container>
            {hasInboxContent ? (
                <PageLayout>
                    {!isMobileAndContactOpened && <NavigationRail filter={filter} scopeHandlers={scopeHandlers} />}
                    <PageGrid contactIsOpen={contactOpened}>
                        {(!isMobile || !activeConversation) && !(isMobileAndContactOpened && contactOpened) && (
                            <Dialogs
                                isMobile={isMobile}
                                filter={filter}
                                setFilter={setFilter}
                                scopeHandlers={scopeHandlers}
                                activeConversation={activeConversation}
                                setActiveConversation={setActiveConversation}
                                activeConversationRef={activeConversationRef}
                            />
                        )}
                        {(!isMobile || activeConversation) && (
                            <Inbox
                                isMobile={isMobile}
                                filterRef={filterRef}
                                setContactOpened={setContactOpened}
                                activeConversation={activeConversation}
                                setActiveConversation={setActiveConversation}
                            />
                        )}
                        {contactOpened ? (
                            <ContactInfo
                                open={contactOpened}
                                onClose={onCloseContact}
                                activeConversation={activeConversation}
                                filterRef={filterRef}
                                setActiveConversation={setActiveConversation}
                                onDeleteCallback={onDeleteContactCallback}
                                onMergeCallback={onMergeContactCallback}
                                onUpdateCallback={onMergeContactCallback}
                            />
                        ) : null}
                    </PageGrid>
                </PageLayout>
            ) : !channelsLoading && !conversationsLoading ? (
                <EmptyLayout>
                    <PageTitle titleKey="Header.messagingInbox" />
                    <PageHeader
                        icon={<InboxEmptyIcon />}
                        title={t('InboxPage.emptyChannelsTitle')}
                        subtitle={t('InboxPage.emptyChannelsSubtitle')}
                    >
                        {!isUser && (
                            <Button variant="contained" onClick={() => navigate(RoutePath.AccountSettingsChannels)}>
                                {t('InboxPage.emptyChannelsButton')}
                            </Button>
                        )}
                    </PageHeader>
                </EmptyLayout>
            ) : null}
            <SubscribeToMessagesSnackbar />
        </Container>
    );
};

export default InboxPage;
